import { translation } from "../libs/api-url";
import axios from "@/libs/axios";

/**
 * Translation Service
 * Handles all translation-related API calls.
 * 
 * API Endpoints:
 * - GET /api/v1/admin/translation/list - Get translation list
 */
class TranslationService {
  /**
   * @name getList
   * @description Fetches translation list from admin API with pagination and search.
   * @param {Object} params - Query parameters (page, limit, search_text)
   * @throws {Error} If the request fails, the error is thrown for the caller to handle.
   * @returns {Promise<Object>} The response data containing the translation list.
   */
  static async getList(params = {}) {
    try {
      const response = await axios.get(translation.list, { params });
      return response.data;
    } catch (err) {
      throw err; // Re-throw the error for the caller to handle
    }
  }

  /**
   * @name add
   * @description Creates a new translation.
   * @param {Object} data - Translation data with key and translations array
   * @throws {Error} If the request fails, the error is thrown for the caller to handle.
   * @returns {Promise<Object>} The response data.
   */
  static async add(data) {
    try {
      const response = await axios.post(translation.add, data);
      return response.data;
    } catch (err) {
      throw err;
    }
  }

  /**
   * @name get
   * @description Fetches a single translation by ID.
   * @param {string} id - Translation UUID
   * @throws {Error} If the request fails, the error is thrown for the caller to handle.
   * @returns {Promise<Object>} The response data containing translation details.
   */
  static async get(id) {
    try {
      const response = await axios.get(translation.get, { params: { id } });
      return response.data;
    } catch (err) {
      throw err;
    }
  }

  /**
   * @name update
   * @description Updates an existing translation.
   * @param {Object} data - Translation data with id, key, and translations array
   * @throws {Error} If the request fails, the error is thrown for the caller to handle.
   * @returns {Promise<Object>} The response data.
   */
  static async update(data) {
    try {
      const response = await axios.put(translation.update, data);
      return response.data;
    } catch (err) {
      throw err;
    }
  }

  /**
   * @name delete
   * @description Deletes a translation by ID.
   * @param {string} id - Translation UUID
   * @throws {Error} If the request fails, the error is thrown for the caller to handle.
   * @returns {Promise<Object>} The response data.
   */
  static async delete(id) {
    try {
      const response = await axios.delete(translation.delete, { params: { id } });
      return response.data;
    } catch (err) {
      throw err;
    }
  }

  /**
   * @name getLanguages
   * @description Fetches available languages for translations.
   * @throws {Error} If the request fails, the error is thrown for the caller to handle.
   * @returns {Promise<Object>} The response data containing languages list.
   */
  static async getLanguages() {
    try {
      const response = await axios.get(translation.getLanguages);
      return response.data;
    } catch (err) {
      throw err;
    }
  }

  /**
   * @name import
   * @description Imports translations from CSV file.
   * @param {Object} data - Import data containing file_data and overwrite_existing flag
   * @throws {Error} If the request fails, error is thrown for the caller to handle.
   * @returns {Promise<Object>} The response data.
   */
  static async import(data) {
    try {
      const response = await axios.post(translation.import, data);
      return response.data;
    } catch (err) {
      throw err;
    }
  }

  /**
   * @name export
   * @description Exports translations to CSV with optional filters.
   * @param {Object} params - Query parameters (language_id, search_text)
   * @throws {Error} If the request fails, error is thrown for the caller to handle.
   * @returns {Promise<Blob>} The response data as a blob for file download.
   */
  static async export(params = {}) {
    try {
      const response = await axios.get(translation.export, { 
        params,
        responseType: 'blob' // Important for file download
      });
      return response.data;
    } catch (err) {
      throw err;
    }
  }

  /**
   * @name sync
   * @description Syncs translations from external source.
   * @param {Object} data - Sync options (is_return, is_api)
   * @throws {Error} If request fails, error is thrown for the caller to handle.
   * @returns {Promise<Object>} The response data.
   */
  static async sync(data) {
    try {
      const response = await axios.post(translation.sync, data);
      return response.data;
    } catch (err) {
      throw err;
    }
  }
}

export { TranslationService };
